import { CellToggleComponent } from '@ag-grid/cell-templates/cell-toggle/cell-toggle.component';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, output } from '@angular/core';
import { ConstructionProjectFeatureKey } from '@construction/construction.enums';
import { ConstructionProjectFeaturesType } from '@construction/models/construction-project.model';
import { ConstructionProjectSignalStore } from '@construction/stores/construction-project/construction-project-signal-store.service';
import { ColDef, GetRowIdFunc, GridApi, GridReadyEvent, IsRowMaster } from 'ag-grid-community';
import { FeatureNamePipePipe } from './feature-name-pipe.pipe';
import { PerspectiveSelectionComponent } from './perspective-selection/perspective-selection.component';
import { AgGridAngular } from 'ag-grid-angular';
import { TbsTitleComponent } from '@ngx-terabase/ui/title';

export enum EnabledFeature {
  OwnerEnabled = 'owner_enabled',
  EpcEnabled = 'epc_enabled',
  SupplierEnabled = 'supplier_enabled',
}

// Features hidden from this table for now. Their values are managed by a backend migration.
// They stay in `rowData` so they are still sent back unchanged on save.
const HIDDEN_FEATURES: ConstructionProjectFeatureKey[] = [
  ConstructionProjectFeatureKey.LOGISTICS_INVENTORY,
  ConstructionProjectFeatureKey.APPROVALS,
  ConstructionProjectFeatureKey.TERAFAB,
];

export type PerspectiveChangeParam = {
  perspectiveChange: (param: ConstructionProjectFeaturesType, role: EnabledFeature) => void;
};

@Component({
  selector: 'construction-features-list',
  templateUrl: './construction-features-list.component.html',
  styleUrls: ['./construction-features-list.component.scss'],
  providers: [FeatureNamePipePipe],
  imports: [TbsTitleComponent, AgGridAngular],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConstructionFeaturesListComponent implements OnInit {
  public readonly enabledProjectFeaturesEmitter = output<ConstructionProjectFeaturesType[]>();
  public readonly projectFeautures = input<ConstructionProjectFeaturesType[]>([]);
  public readonly rowData = computed(() => this.composeRowData(this.projectFeautures()));
  public readonly visibleRowData = computed(() =>
    this.rowData().filter((feature) => !HIDDEN_FEATURES.includes(feature.name)),
  );
  public featuresEnum = ConstructionProjectFeatureKey;
  public enabledFeatureEnum = EnabledFeature;

  public detailCellRenderer = PerspectiveSelectionComponent;
  public detailCellRendererParams: PerspectiveChangeParam;

  public gridApi: GridApi<ConstructionProjectFeaturesType>;

  public defaultColDef: ColDef = {
    flex: 1,
    minWidth: 90,
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
  };

  public columnDefs: ColDef[] = [
    {
      field: '',
      valueFormatter: () => '',
      headerValueGetter: () => '',
      headerName: '',
      minWidth: 50,
      cellRenderer: 'agGroupCellRenderer',
    },
    {
      field: 'name',
      headerName: 'Feature',
      minWidth: 200,
      sortable: false,
      valueGetter: (params) => this.featureNamePipe.transform(params.data.name),
    },
    {
      field: 'owner_enabled',
      headerName: 'Owner',
      maxWidth: 100,
      sortable: false,
      cellRendererSelector: (params) => {
        const { data, value } = params;

        return {
          component: CellToggleComponent,
          params: {
            tooltip: data.tooltipMessage,
            isChecked: value,
            isDisabled: data.isDisabled,
            change: (checked: boolean) => {
              data.owner_enabled = checked;
              this.featureToggled(data, this.enabledFeatureEnum.OwnerEnabled);
            },
          },
        };
      },
    },
    {
      field: 'epc_enabled',
      headerName: 'EPC',
      maxWidth: 100,
      sortable: false,
      cellRendererSelector: (params) => {
        const { data, value } = params;

        return {
          component: CellToggleComponent,
          params: {
            tooltip: data.tooltipMessage,
            isChecked: value,
            isDisabled: data.isDisabled,
            change: (checked: boolean) => {
              data.epc_enabled = checked;
              this.featureToggled(data, this.enabledFeatureEnum.EpcEnabled);
            },
          },
        };
      },
    },
    {
      field: 'supplier_enabled',
      headerName: 'Supplier',
      // Hidden while Logistics & Inventory (the only supplier-enabled feature) is hidden.
      hide: true,
      maxWidth: 100,
      sortable: false,
      cellRendererSelector: (params) => {
        const { data, value } = params;
        const { name } = data;

        return {
          component: CellToggleComponent,
          params: {
            tooltip: 'Feature is not supported for Supplier',
            isChecked: value,
            isDisabled: !(name === this.featuresEnum.LOGISTICS_INVENTORY),
            change: (checked: boolean) => {
              data.supplier_enabled = checked;
              this.featureToggled(data, this.enabledFeatureEnum.SupplierEnabled);
            },
          },
        };
      },
    },
  ];

  private featureNamePipe = inject(FeatureNamePipePipe);
  private constructionProjectStoreService = inject(ConstructionProjectSignalStore);
  private constructionProject = this.constructionProjectStoreService.project();

  public ngOnInit(): void {
    this.detailCellRendererParams = {
      perspectiveChange: this.perspectiveChange,
    };
  }

  public onGridReady(params: GridReadyEvent<ConstructionProjectFeaturesType>) {
    this.gridApi = params.api;
  }

  // Row ids are feature names, so row lookups don't depend on which rows are hidden.
  public getRowId: GetRowIdFunc<ConstructionProjectFeaturesType> = (params) => params.data.name;

  public isRowMaster: IsRowMaster = (params: ConstructionProjectFeaturesType) => {
    return params.name === this.featuresEnum.DIGITAL_TWIN;
  };

  public perspectiveChange = (feature: ConstructionProjectFeaturesType, role: EnabledFeature) => {
    feature[role] = !(!feature.aerial_data![role] && !feature.ground_data![role]);

    const rowToUpdate = this.gridApi.getRowNode(feature.name);

    if (rowToUpdate) {
      this.gridApi.redrawRows({ rowNodes: [rowToUpdate] });
    }

    this.constructionProjectStoreService.updateFeatureFlags(this.rowData());
    this.enabledProjectFeaturesEmitter.emit(this.rowData());
  };

  private featureToggled(feature: ConstructionProjectFeaturesType, featureRoleEnabled: EnabledFeature): void {
    if (feature.name === this.featuresEnum.DIGITAL_TWIN) {
      feature.aerial_data![featureRoleEnabled] = feature[featureRoleEnabled];
      feature.ground_data![featureRoleEnabled] = feature[featureRoleEnabled];

      const rowToUpdate = this.gridApi.getRowNode(feature.name);

      if (rowToUpdate) {
        this.gridApi.redrawRows();
      }
    }

    this.constructionProjectStoreService.updateFeatureFlags(this.rowData());
    this.enabledProjectFeaturesEmitter.emit(this.rowData());
  }

  private composeRowData(features: ConstructionProjectFeaturesType[] = []) {
    const rowData: (ConstructionProjectFeaturesType & { isDisabled: boolean; tooltipMessage: string })[] = features.map(
      (feature) => {
        let isDisabled = false;
        let tooltipMessage = '';

        if (
          feature.name === ConstructionProjectFeatureKey.OMEGA &&
          !this.constructionProject.wbs_config?.published_at
        ) {
          isDisabled = true;
          tooltipMessage = 'Please publish Activity List to enable this feature';
        }

        if (
          [
            ConstructionProjectFeatureKey.PRODUCTION_TRACKING,
            ConstructionProjectFeatureKey.EQUIPMENT_TRACKING,
          ].includes(feature.name)
        ) {
          isDisabled = !this.constructionProject.project_workhours.length;
          tooltipMessage = 'Please set Working Hours in Schedule to enable this feature';
        }

        return { ...feature, isDisabled, tooltipMessage };
      },
    );

    return rowData.sort((a, b) => a.id - b.id);
  }
}
